import React, { useState } from "react";
import { Button, Popconfirm, message } from "antd";
import { useLocation } from "react-router-dom";
import { republishClipApi } from "api/fastApi";
import { useIsEnglishUi } from "utils/uiLanguage";

/**
 * Cut-videos clip "Reprocess & republish" action. Shown only when the /fast editor was opened for a clip
 * (URL has ?clip={clip_id}). It rebuilds clip_subs.vtt from the clip's OWN edited subtitles and pushes it
 * to R2 (via translate-python -> site_server), then deletes the on-disk materialization so the clip lives
 * only on R2 again. The subtitles are NEVER re-sliced from the lecture, so clip-specific edits survive.
 */
export const ReprocessClip: React.FC = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const clip = params.get("clip");
    // Republish the language currently being edited so an EN/DE/… edit publishes that language, not RU.
    // The URL `lang` is already a backend code (en/ru/de/es/zh/ar); default ru.
    const lang = params.get("lang") || "ru";
    // Source the target was translated from (en when the URL flags from_eng, else ru). The backend finds
    // the edited chunks regardless of source folder, so this just points it at the right place first.
    const source = params.get("from_eng") === "1" ? "en" : "ru";
    const [loading, setLoading] = useState(false);
    // Note: `from_eng` above picks the API's source language, not the reader's — the panel's
    // language is a separate question and is answered in one place for all of it.
    const en = useIsEnglishUi();

    if (!clip) return null;

    const onConfirm = async () => {
        setLoading(true);
        try {
            const res = await republishClipApi(clip, lang, source);
            message.success(
                en
                    ? `Clip republished — subtitles updated (${res.n_cues} lines). ` +
                          "Reload the clip page to see the fix."
                    : `Клип переопубликован — субтитры обновлены (${res.n_cues} строк). ` +
                          "Перезагрузите страницу клипа, чтобы увидеть исправление.",
            );
        } catch (e) {
            const msg = e instanceof Error ? e.message : en ? "error" : "ошибка";
            message.error((en ? "Could not republish: " : "Не удалось переопубликовать: ") + msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Popconfirm
            title={
                en
                    ? "Rebuild and republish the clip from the current subtitles?"
                    : "Пересобрать и переопубликовать клип из текущих субтитров?"
            }
            description={
                en
                    ? "The clip's subtitles on R2 will be replaced by your edit; the translation (EN) is marked stale."
                    : "Субтитры клипа на R2 будут заменены вашей правкой, перевод (EN) помечен устаревшим."
            }
            okText={en ? "Yes, publish" : "Да, опубликовать"}
            cancelText={en ? "Cancel" : "Отмена"}
            onConfirm={onConfirm}
        >
            <Button type="primary" danger loading={loading}>
                {en ? "⟳ Rebuild and publish" : "⟳ Пересобрать и опубликовать"}
            </Button>
        </Popconfirm>
    );
};
